import React, { useEffect, useRef, useState } from 'react';
import { FiMoreVertical, FiEye, FiEdit, FiPrinter, FiCheckCircle, FiXCircle, FiTrash2 } from "react-icons/fi";
import ReactDOM from 'react-dom';
import ViewDetailAppointment from '../Popup/ViewDetailAppointment';
import EditAppointment from '../Popup/EditAppointment';
import AppointmentService from '../services/appointmentTimelineService';

const ActionTableButton = ({ 
  item, 
  refreshData,
  viewModalTarget = "viewAppointmentDetailModal" 
}) => {
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const modalRef = useRef(null);
  
  // States
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [appointments, setAppointments] = useState([]);
  
  // Create service instance
  const appointmentService = new AppointmentService();
  
  // Function to update a specific item locally
  const updateItemLocally = (id, updatedFields) => {
    setAppointments(prevAppointments => 
      prevAppointments.map(appointment => 
        (appointment.id === id || appointment._id === id) 
          ? { ...appointment, ...updatedFields } 
          : appointment
      )
    );
  };

  // Determine if the appointment is active or not
  const isActive = item.status && item.status !== 'Cancelled';

  // Calculate dropdown position
  const calculatePosition = () => {
    if (!buttonRef.current) return { top: 0, left: 0 };
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    
    return {
      top: buttonRect.bottom + scrollTop,
      left: buttonRect.right - 150 + scrollLeft, // 150px is the approximate width of the dropdown
    };
  };
  
  // Handle action button click
  const toggleDropdown = (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Calculate and set position before showing
    const position = calculatePosition();
    setDropdownPosition(position);
    setDropdownVisible(!dropdownVisible);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownVisible && 
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current && 
        !buttonRef.current.contains(event.target)
      ) {
        setDropdownVisible(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownVisible]);
  
  // Effect to handle scroll lock when modal is open
  useEffect(() => {
    if (showModal) {
      // Prevent scroll on body
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '15px'; // Compensate for scrollbar
    } else {
      // Restore scroll
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
    
    return () => {
      // Cleanup when unmounting
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, [showModal]);
  
  // Action functions
  const handleViewDetails = (e) => {
    e.stopPropagation();
    console.log("View details for appointment:", item.id || item._id);
    setSelectedAppointment(item);
    setIsEditMode(false);
    setShowModal(true);
    setDropdownVisible(false);
  };
  
  const closeModal = () => {
    setShowModal(false);
    setSelectedAppointment(null);
    setIsEditMode(false);
  };
  
  const handleEdit = (e) => {
    e.stopPropagation();
    console.log('Edit appointment with ID:', item.id || item._id);
    // Prepare appointment object with a signal to indicate edit mode
    const appointmentWithEditMode = { ...item, initialEditMode: true };
    setSelectedAppointment(appointmentWithEditMode);
    setIsEditMode(true);
    setShowModal(true);
    setDropdownVisible(false);
  };

  const handleAccept = async (e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to accept this appointment?')) {
      try {
        const appointmentId = item.id || item._id;
        console.log('Accept appointment with ID:', appointmentId);
        
        // Use the service to accept the appointment
        const response = await appointmentService.acceptAppointment(appointmentId);
        
        console.log('Accept response:', response);
        alert('Appointment accepted successfully');
        
        // Update local state
        updateItemLocally(appointmentId, { isaccepted: true, status: 'Confirmed' });
        
        // If there's a refreshData function, call it
        if (refreshData) {
          refreshData();
        }
      } catch (error) {
        console.error('Error accepting appointment:', error);
        alert(`Failed to accept appointment: ${error.message}`);
      }
      
      setDropdownVisible(false);
    }
  };

  const handleReject = async (e) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to reject this appointment?')) {
      try {
        const appointmentId = item.id || item._id;
        console.log('Reject appointment with ID:', appointmentId);
        
        // Use the service to cancel the appointment (reject)
        const response = await appointmentService.cancelAppointment(appointmentId, {
          reason: 'Rejected by provider',
          notes: 'Appointment was rejected'
        });
        
        console.log('Reject response:', response);
        alert('Appointment rejected successfully');
        
        // Update local state
        updateItemLocally(appointmentId, { isaccepted: false, status: 'Cancelled' });
        
        // If there's a refreshData function, call it
        if (refreshData) {
          refreshData();
        }
      } catch (error) {
        console.error('Error rejecting appointment:', error);
        alert(`Failed to reject appointment: ${error.message}`);
      }
      
      setDropdownVisible(false);
    }
  };
  
  const handlePrint = (e) => {
    e.stopPropagation();
    console.log('Print appointment with ID:', item.id || item._id);
    
    // Create a new window for printing
    const printWindow = window.open('', '', 'width=800,height=600');
    
    // Format dates for display
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      try {
        return new Date(dateString).toLocaleDateString();
      } catch {
        return dateString;
      }
    };

    const formatTime = (timeString) => {
      if (!timeString) return "N/A";
      return timeString;
    };

    // Create HTML content for printing with all available data
    printWindow.document.write(`
      <html>
        <head>
          <title>Appointment Details</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px;
            }
            .print-header { 
              text-align: center; 
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 10px;
            }
            h1 { 
              margin-bottom: 5px;
            }
            .appointment-id {
              font-size: 14px;
              color: #666;
              margin-bottom: 20px;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 10px;
              border-bottom: 1px solid #ddd;
              padding-bottom: 5px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px; 
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 8px; 
              text-align: left; 
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold; 
            }
            .patient-doctor-info {
              display: flex;
              justify-content: space-between;
            }
            .patient-info, .doctor-info {
              width: 48%;
            }
            .status-badge {
              padding: 4px 8px;
              border-radius: 4px;
              font-weight: bold;
              color: white;
              background-color: ${
                item.status === 'Confirmed' ? '#28a745' : 
                item.status === 'Pending' ? '#ffc107' : 
                item.status === 'Cancelled' ? '#dc3545' : 
                '#6c757d'
              };
            }
            .footer {
              margin-top: 40px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              font-size: 12px;
              text-align: center;
              color: #666;
            }
            @media print {
              .page-break {
                page-break-before: always;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>Appointment Details</h1>
            <div class="appointment-id">Appointment ID: ${item.appointmentId || item.id || item._id}</div>
            <div>Date: ${formatDate(item.date)}</div>
            <div>Time: ${formatTime(item.startTime)} - ${formatTime(item.endTime)}</div>
          </div>
          
          <div class="patient-doctor-info">
            <div class="patient-info section">
              <div class="section-title">Patient Information</div>
              <p><strong>Name:</strong> ${item.patientName || "N/A"}</p>
              <p><strong>Date of Birth:</strong> ${formatDate(item.dateOfBirth)}</p>
              <p><strong>Address:</strong> ${item.address || "N/A"}</p>
              <p><strong>User ID:</strong> ${item.userid || "N/A"}</p>
            </div>
            
            <div class="doctor-info section">
              <div class="section-title">Healthcare Provider</div>
              <p><strong>Doctor:</strong> ${item.doctorName || "N/A"}</p>
              <p><strong>Role:</strong> ${item.doctorRole || "N/A"}</p>
            </div>
          </div>
          
          <div class="section">
            <div class="section-title">Appointment Information</div>
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Start Time</th>
                  <th>End Time</th>
                  <th>Accepted</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>${item.appointmentType || "N/A"}</td>
                  <td><span class="status-badge">${item.status || "N/A"}</span></td>
                  <td>${formatDate(item.date)}</td>
                  <td>${formatTime(item.startTime)}</td>
                  <td>${formatTime(item.endTime)}</td>
                  <td>${item.isaccepted ? "Yes" : "No"}</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="section">
            <div class="section-title">Additional Information</div>
            <p><strong>Medical History:</strong> ${item.medicalHistory || "None"}</p>
            <p><strong>Notes:</strong> ${item.notes || "None"}</p>
            <p><strong>Check-in Time:</strong> ${item.checkInTime ? new Date(item.checkInTime).toLocaleString() : "N/A"}</p>
            <p><strong>Wait Time:</strong> ${item.waitTime ? item.waitTime + " minutes" : "N/A"}</p>
            <p><strong>Consultation Duration:</strong> ${item.consultationDuration ? item.consultationDuration + " minutes" : "N/A"}</p>
          </div>
          
          <div class="section">
            <div class="section-title">Meeting Information</div>
            <p><strong>Meeting User ID:</strong> ${item.meetinguserid || "N/A"}</p>
            <p><strong>Created:</strong> ${formatDate(item.createdAt)}</p>
            <p><strong>Last Updated:</strong> ${formatDate(item.updatedAt)}</p>
          </div>
          
          <div class="footer">
            <p>This appointment record was generated on ${new Date().toLocaleString()}</p>
            <p>Please keep this record for future reference and medical appointments.</p>
          </div>
          
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              }
            }
          </script>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    setDropdownVisible(false);
  };
  
  const handleDelete = async (e) => {
    e.stopPropagation();
    
    const appointmentId = item.id || item._id;
    const appointmentName = item.patientName || 'this appointment';
    
    if (window.confirm(`Are you sure you want to delete the appointment for ${appointmentName}?`)) {
      try {
        console.log('Deleting appointment with ID:', appointmentId);
        
        // Use the service to delete the appointment
        const response = await appointmentService.deleteAppointment(appointmentId);
        
        console.log('Delete response:', response);
        alert('Appointment deleted successfully');
        
        // Close dropdown
        setDropdownVisible(false);
        
        // Always reload the page after successful deletion
        window.location.reload();
        
      } catch (error) {
        console.error('Error deleting appointment:', error);
        
        // More descriptive error message
        const errorMessage = error.message.includes('fetch') 
          ? 'Network error. Please check your connection and try again.'
          : `Failed to delete appointment: ${error.message}`;
          
        alert(errorMessage);
        
        // Close dropdown even on error
        setDropdownVisible(false);
      }
    }
  };
  
  const handleUpdateSuccess = async (updatedAppointment) => {
    try {
      console.log('Preparing to update appointment:', updatedAppointment);
      
      // Format the data according to what backend expects
      const formattedData = {
        _id: updatedAppointment._id,
        appointmentId: updatedAppointment.appointmentId,
        patientName: updatedAppointment.patientName,
        patientAvatar: updatedAppointment.patientAvatar,
        dateOfBirth: updatedAppointment.dateOfBirth,
        address: updatedAppointment.address,
        userid: updatedAppointment.userid,
        meetinguserid: updatedAppointment.meetinguserid,
        doctorName: updatedAppointment.doctorName,
        doctorRole: updatedAppointment.doctorRole,
        date: updatedAppointment.date,
        startTime: updatedAppointment.startTime,
        endTime: updatedAppointment.endTime,
        status: updatedAppointment.status,
        medicalHistory: updatedAppointment.medicalHistory,
        appointmentType: updatedAppointment.appointmentType,
        notes: updatedAppointment.notes,
        checkInTime: updatedAppointment.checkInTime,
        waitTime: updatedAppointment.waitTime,
        consultationDuration: updatedAppointment.consultationDuration,
        isaccepted: updatedAppointment.isaccepted
      };
      
      console.log('Sending formatted data to API:', formattedData);
      
      // Use the service to update the appointment
      const appointmentId = updatedAppointment._id || updatedAppointment.id;
      const response = await appointmentService.updateAppointment(appointmentId, formattedData);
      
      console.log('Update response:', response);
      
      if (response) {
        alert('Appointment updated successfully');
        if (refreshData) refreshData();
        closeModal();
      } else {
        throw new Error('Failed to update appointment - no response');
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      alert(`Update failed: ${error.message}`);
    }
  };
  
  const handleCancel = () => {
    // Handle cancellation
    console.log('Edit cancelled');
    // Close the modal
    closeModal();
  };
  
  // Dropdown component that will render in portal
  const DropdownMenu = () => {
    if (!dropdownVisible) return null;
    
    return ReactDOM.createPortal(
      <div 
        ref={dropdownRef}
        className="dropdown-menu show"
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          zIndex: 9999,
          minWidth: '150px',
          backgroundColor: '#fff',
          boxShadow: '0 2px 10px rgba(0,0,0,.2)',
          border: '1px solid rgba(0,0,0,.15)',
          borderRadius: '4px',
          padding: '0.5rem 0'
        }}
      >
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handleViewDetails}
        >
          <FiEye className="me-2" /> View Details
        </button>
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handleEdit}
        >
          <FiEdit className="me-2" /> Edit
        </button>
        <button 
          className="dropdown-item d-flex align-items-center" 
          type="button"
          onClick={handlePrint}
        >
          <FiPrinter className="me-2" /> Print
        </button>
        {!item.isaccepted && item.status !== 'Confirmed' && (
          <button 
            className="dropdown-item d-flex align-items-center text-success" 
            type="button"
            onClick={handleAccept}
          >
            <FiCheckCircle className="me-2" /> Confirm
          </button>
        )}
        {item.status !== 'Cancelled' && (
          <button 
            className="dropdown-item d-flex align-items-center text-warning" 
            type="button"
            onClick={handleReject}
          >
            <FiXCircle className="me-2" /> Cancel
          </button>
        )}
        <button 
          className="dropdown-item d-flex align-items-center text-danger" 
          type="button"
          onClick={handleDelete}
        >
          <FiTrash2 className="me-2" /> Delete
        </button>
      </div>,
      document.body
    );
  };
  
  // Modal component for viewing details
  const DetailModal = () => {
    if (!showModal) return null;
    
    // Using portal to render the modal outside the normal DOM flow
    return ReactDOM.createPortal(
      <div className="modal-backdrop-custom">
        <div 
          ref={modalRef}
          className="modal-custom" 
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.5)'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div 
            className="modal-dialog modal-lg" 
            style={{
              maxWidth: '800px',
              width: '100%',
              margin: '10px',
              backgroundColor: '#fff',
              borderRadius: '5px',
              boxShadow: '0 5px 15px rgba(0,0,0,.5)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh' 
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '90vh',
              overflow: 'hidden'
            }}>
              <div className="modal-header" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '1rem',
                borderBottom: '1px solid #dee2e6'
              }}>
                <h5 className="modal-title">
                  {isEditMode ? 'Edit Appointment' : 'Appointment Details'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={closeModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body" style={{
                padding: '1rem',
                overflow: 'auto'
              }}>
                 {selectedAppointment && !isEditMode && ( 
                  <ViewDetailAppointment 
                    appointment={selectedAppointment} 
                  />
                )}
                {selectedAppointment && isEditMode && (
                  <EditAppointment 
                    appointment={selectedAppointment} 
                    onUpdateSuccess={handleUpdateSuccess}
                    onCancel={handleCancel}
                  />
                )}
              </div>
              <div className="modal-footer" style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '1rem',
                borderTop: '1px solid #dee2e6'
              }}>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };
  
  return (
    <>
      <div onClick={e => e.stopPropagation()}>
        <button 
          ref={buttonRef}
          className="btn btn-sm" 
          type="button" 
          onClick={toggleDropdown}
          aria-expanded={dropdownVisible}
        >
          <FiMoreVertical />
        </button>
      </div>
      
      {/* Render dropdown menu through portal */}
      <DropdownMenu />
      
      {/* Render modal through portal */}
      <DetailModal />
    </>
  );
};

export default ActionTableButton;